import { ReactNode } from 'react';
import { Icon } from './Icon';
import { cn } from '@/utils/cn';
import type { IconName } from '@/constants/icons';

export interface EmptyStateProps {
  /** HappyIcon name (with or without 'hm-' prefix), e.g. ICONS.BELL */
  icon?: IconName;
  /** Main message, e.g. "No notifications found" */
  title: string;
  /** Optional supporting copy */
  description?: string;
  /** Optional call-to-action slot, e.g. a <Button> */
  action?: ReactNode;
  /** Additional classes appended to the base styles. Note: not guaranteed to override conflicting base utilities (cn is a concat, not tailwind-merge) — prefer additive classes. */
  className?: string;
}

/**
 * EmptyState - Centered empty-state block with a muted icon in a soft
 * circle, title, optional description, and optional CTA slot. Replaces
 * both the bare "No X found" table rows and Notifications.tsx's richer
 * icon+message+CTA empty state.
 *
 * @example
 * // Bare "no results" (table row wrapper still owned by the caller)
 * <EmptyState title="No users found" />
 *
 * @example
 * // Rich empty state with icon + CTA
 * <EmptyState
 *   icon={ICONS.BELL}
 *   title="No notifications found"
 *   description="Broadcast notifications you create will show up here."
 *   action={<Button onClick={handleCreateClick} size="sm">Create First Notification</Button>}
 * />
 */
export const EmptyState = ({
  icon,
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) => {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center py-12 px-4', className)}>
      {icon && (
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Icon name={icon} size="lg" className="text-gray-400" />
        </div>
      )}
      <p className="font-medium text-gray-900">{title}</p>
      {description && (
        <p className="text-sm text-gray-500 mt-1 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};

export default EmptyState;
